import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import { DepartmentTreeNode } from '@/interfaces/model/general/department';
import { CompanyDepartmentDeletionImpact } from '@/interfaces/api/general/company';
import { usePermissionComposable } from '@/composables/usePermission';
import { useDepartmentComposable } from '@/composables/useDepartment';
import DepartmentEditDialog from '@/views/pages/companyDepartments/DepartmentEditDialog.vue';
import { collectParentIds, flattenTree } from '@/utils/departmentTree';
import { defineMessages } from '@/lib/i18n';
import { i18n } from '@/plugins/vue-i18n';
import sharedMessages from '@/lib/shared-messages';


export default /*#__PURE__*/_defineComponent({
  __name: 'CompanyDepartmentsTable',
  props: {
    tree: null
  },
  emits: ["changed", "click-user-count", "click-sds-count"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'changed'): void;
  (e: 'click-user-count', node: DepartmentTreeNode): void;
  (e: 'click-sds-count', node: DepartmentTreeNode): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  tree: DepartmentTreeNode[];
};

const $t = i18n.t.bind(i18n);

const m = defineMessages({
  userCount: 'ユーザー数',
  sdsCount: 'SDS数',
  userCountValue: '{count}人',
  deleteDepartment: '部署の削除',
  deleteDepartmentTitle: '{name}を削除しますか？',
  deleteBlockedByChildren:
    '配下に子部署が存在するため削除できません。先に子部署を削除または移動してください。',
  deleteImpactIntro: 'この部署には以下が紐付いています:',
  deleteImpactUsers: 'メンバー: {count}名',
  deleteImpactSdses: 'SDS: {count}件',
  deleteImpactUnassigned:
    '削除すると、これらは「未所属」になります。削除後に管理者が部署を再割り当てしてください。',
  noDepartments: '部署がまだ登録されていません。',
  expand: '子部署を表示',
  collapse: '子部署を折りたたむ',
  expandAll: 'すべてを開く',
  collapseAll: 'すべてを閉じる',
});





const { hasPermission, PERMISSIONS: P } = usePermissionComposable();
const {
  isDeleting,
  deleteCompanyDepartment,
  getDeletionImpact,
  isInEditableScope,
} = useDepartmentComposable();

// Mirrors the backend's edit scope (app/utils/department_scope.py):
// DEPARTMENT_EDIT_OTHER_DEPARTMENT bypasses the scope check entirely,
// otherwise a department is only editable within the user's own
// department(s) and their descendants.
const isInEditScope = (node: DepartmentTreeNode): boolean =>
  hasPermission(P.DEPARTMENT_EDIT_OTHER_DEPARTMENT) ||
  isInEditableScope(node.uuid);

// 既定は全展開。折りたたんだものだけを持つので、部署が増えても既定の見た目は変わらない。
const collapsedIds = ref<Set<string>>(new Set());
const isCollapsed = (uuid: string) => collapsedIds.value.has(uuid);
const toggle = (uuid: string) => {
  const next = new Set(collapsedIds.value);
  if (next.has(uuid)) {
    next.delete(uuid);
  } else {
    next.add(uuid);
  }
  collapsedIds.value = next;
};

const hasCollapsibleNodes = computed(
  () => collectParentIds(props.tree).length > 0
);
const expandAll = () => {
  collapsedIds.value = new Set();
};
const collapseAll = () => {
  collapsedIds.value = new Set(collectParentIds(props.tree));
};

const rows = computed(() => flattenTree(props.tree, collapsedIds.value));

/**
 * 操作ボタンの表示はホバー中の行だけ (Figma 準拠)。
 * CSS の :hover ではなく state で持つのは、合成イベントでも再現できるようにするため
 * (:hover は Cypress の trigger('mouseenter') では発火しない)。
 */
const hoveredId = ref<string>();

const emitUserCountClick = (node: DepartmentTreeNode) =>
  emit('click-user-count', node);
const emitSdsCountClick = (node: DepartmentTreeNode) =>
  emit('click-sds-count', node);

const showEditDialog = ref(false);
const updateTarget = ref<DepartmentTreeNode>();

const showUpdateDialog = (node: DepartmentTreeNode) => {
  updateTarget.value = node;
  showEditDialog.value = true;
};
const onEdited = () => {
  updateTarget.value = undefined;
  emit('changed');
};

// キャンセルで閉じたときも対象をクリアする。
// v-dialog は内容を DOM に残すため、対象を持ったままだと閉じたダイアログ内に
// 部署名が残り、一覧側と二重にマッチする。
watch(showEditDialog, (isOpen) => {
  if (!isOpen) {
    updateTarget.value = undefined;
  }
});

const showDeleteDialog = ref(false);
const deleteTarget = ref<DepartmentTreeNode>();
const deleteImpact = ref<CompanyDepartmentDeletionImpact>();
const impactLoadingFor = ref<string>();

/**
 * 削除前に影響範囲を問い合わせる。
 * 子部署の有無と、未所属になるメンバー / SDS の件数をダイアログで先に見せる。
 */
const openDeleteDialog = async (node: DepartmentTreeNode) => {
  impactLoadingFor.value = node.uuid;
  try {
    const impact = await getDeletionImpact(node.uuid);
    if (!impact) {
      return;
    }
    deleteTarget.value = node;
    deleteImpact.value = impact;
    showDeleteDialog.value = true;
  } finally {
    impactLoadingFor.value = undefined;
  }
};
const closeDeleteDialog = () => {
  if (isDeleting.value) {
    return;
  }
  showDeleteDialog.value = false;
  deleteTarget.value = undefined;
  deleteImpact.value = undefined;
};
const deleteDepartment = async () => {
  if (!deleteTarget.value || isDeleting.value) {
    return;
  }
  const succeeded = await deleteCompanyDepartment(deleteTarget.value.uuid);
  if (!succeeded) {
    return;
  }
  closeDeleteDialog();
  emit('changed');
};

return { __sfc: true,$t, m, props, emit, hasPermission, P, isDeleting, deleteCompanyDepartment, getDeletionImpact, isInEditableScope, isInEditScope, collapsedIds, isCollapsed, toggle, hasCollapsibleNodes, expandAll, collapseAll, rows, hoveredId, emitUserCountClick, emitSdsCountClick, showEditDialog, updateTarget, showUpdateDialog, onEdited, showDeleteDialog, deleteTarget, deleteImpact, impactLoadingFor, openDeleteDialog, closeDeleteDialog, deleteDepartment, DepartmentEditDialog, sharedMessages }
}

})